import type { Chart, ChartValue } from 'features/charts-v2/types'
import type { ChartPanel } from 'features/charts-v2/types/filter-hash/v2'
import type {
  Breakdown as YogiQLBreakdown,
  ChartRequest,
  ChartRunResponse,
  Metric,
  PlainMetric,
  ResultCell,
  ResultColumn,
} from 'features/charts-v2/types/yogiql'
import type { YogiQLChartSpec } from 'features/charts-v2/types/yogiql-chart-specs'
import {
  breakdownKey,
  YOGIQL_ROW_CEILING,
  type YogiQLNames,
  metricKey,
  NOT_PROMOTIONAL,
  panelBreakdownOrder,
  toYogiQLMetric,
} from 'features/charts-v2/utils/to-yogiql-chart-request'

// YogiQL spellings back to the ones the renderer and the drill-downs expect.
function relabel(column: ResultColumn, cell: ResultCell): string | number {
  // A null promotion is the panel's Not Promotional row (06 §29).
  if (column.key === 'promotion' && cell === null) return NOT_PROMOTIONAL
  if (cell === null) return cell as unknown as string
  if (column.bucket === 'polarity' && typeof cell === 'string') {
    return cell.charAt(0).toUpperCase() + cell.slice(1)
  }
  if (column.bucket === 'star') return Number(cell)
  // A bin comes back as its start (-0.3); the renderer reads SENTIMENT_BIN's index (-3).
  if (column.bucket === 'bin') return Math.round(Number(cell) * 10)
  return cell
}

const isPlain = (metric: Metric): metric is PlainMetric => 'field' in metric

// A sankey states a grouped column as the group then its members; the renderer
// reads the members plus a mapping keyed by the legacy wire axis.
const SANKEY_GROUP_AXIS: Record<string, string> = {
  parent_theme: 'cluster_topic',
  brand_group: 'product_brand',
  product_group: 'product_hierarchy',
}

/**
 * Shape a YogiQL result into the legacy `Chart` the renderer reads: category
 * in panel order (time last), values as the panel's metrics then the ones the
 * request appended, legacy display spellings, and the render-cap signal. Pure.
 */
export function toLegacyChart(
  response: ChartRunResponse,
  request: ChartRequest,
  panel: ChartPanel,
  spec: YogiQLChartSpec,
  context: { names?: YogiQLNames } = {},
): Chart {
  const breakdownColumns = response.columns
    .map((column, index) => ({ column, index }))
    .filter(({ column }) => column.role === 'breakdown')
  const metricColumns = response.columns
    .map((column, index) => ({ column, index }))
    .filter(({ column }) => column.role === 'metric' && column.pivot === null)
  const metricOffset = breakdownColumns.length

  // The request's breakdowns, read in panel order by their roles, are the one
  // source of the category keys; time (line) sits last, as the legacy wire.
  const breakdowns = request.breakdowns ?? []
  const groups =
    spec.name === 'sankey'
      ? breakdowns.flatMap((group, i) => {
          const member = breakdowns[i + 1]
          const axis = SANKEY_GROUP_AXIS[group.field]
          return axis && member ? [{ axis, group, member }] : []
        })
      : []
  const groupKeys = groups.map(({ group }) => breakdownKey(group))
  const categoryKeys = panelBreakdownOrder(spec, breakdowns.length)
    .map((i) => breakdownKey(breakdowns[i]!))
    .filter((key) => !groupKeys.includes(key))
  const categoryColumns = categoryKeys.map((key) =>
    breakdownColumns.find(({ column }) => column.key === key),
  )

  // Values keep the panel's positions (rating and NPS repeat one column),
  // then the metrics the request appended. A ridgeline's or sankey's request
  // is its wire order, [count, avg], which the renderer reads.
  const appended = request.metrics.filter(isPlain).map(metricKey)
  const panelKeys = panel.metrics.map(toYogiQLMetric).map(metricKey)
  const wireOrder = spec.name === 'ridgeline' || spec.name === 'sankey'
  const valueKeys = wireOrder ? [] : panelKeys
  for (const key of appended) if (!valueKeys.includes(key)) valueKeys.push(key)
  const valueColumns = valueKeys.map((key) =>
    metricColumns.find(({ column }) => column.key === key),
  )

  const rows: ChartValue[] = response.data.map((row) => ({
    category: categoryColumns.map((found) =>
      found
        ? relabel(found.column, row.category[found.index] ?? null)
        : (null as unknown as string),
    ),
    values: valueColumns.map((found) =>
      found ? (row.values[found.index - metricOffset] as number | null) : null,
    ),
  }))

  // The drill-down expands an ownership bucket through the legacy mapping,
  // keyed by the wire column and listing each bucket's member brands.
  const hasColumn = (key: string): boolean =>
    breakdownColumns.some(({ column }) => column.key === key)
  const brands = context.names?.brandOwnership
  const themeParents = context.names?.themeParents
  const categoryMapping: Record<string, Record<string, string[]>> = {}
  if (brands && hasColumn('brand_group')) {
    categoryMapping['product_brand'] = {
      'My Brands': brands.mine,
      'Competitor Brands': brands.competitor,
    }
  }
  // A grouped theme's axis is keyed by the leaf column the legacy query
  // grouped on, and each parent lists its `Parent: Child` member names.
  if (themeParents && hasColumn('parent_theme')) {
    categoryMapping['cluster_topic'] = themeParents
  }
  // A sankey's mapping is read off its rows: exactly the pairs it draws.
  const columnOf = (
    breakdown: YogiQLBreakdown,
  ): (typeof breakdownColumns)[number] | undefined =>
    breakdownColumns.find(
      ({ column }) => column.key === breakdownKey(breakdown),
    )
  for (const { axis, group, member } of groups) {
    const from = columnOf(group)
    const to = columnOf(member)
    if (!from || !to) continue
    const members: Record<string, string[]> = {}
    for (const row of response.data) {
      const g = relabel(from.column, row.category[from.index] ?? null)
      const m = relabel(to.column, row.category[to.index] ?? null)
      if (g === null || m === null) continue
      const list = members[String(g)] ?? []
      if (!list.includes(String(m))) list.push(String(m))
      members[String(g)] = list
    }
    categoryMapping[axis] = members
  }

  return {
    identifier: { aggregated_values: [], constraints: [], domains: [] },
    data: rows,
    parent_data: null,
    category_mapping: Object.keys(categoryMapping).length
      ? categoryMapping
      : null,
    // The only server-side "too many" signal; the page keeps its own rendered-row cap.
    overDatapointLimit:
      response.page.has_next && request.limit === YOGIQL_ROW_CEILING,
  }
}
